<template>
	<div class="app-loading" v-if="isAppLoading">
		<AppLoading></AppLoading>
	</div>
	<router-view v-else />
</template>

<script lang="ts">
import router from '@/router';
import { useAPIStore } from '@/stores/APIStore';
import { useAuthStore } from '@/stores/AuthStore';
import { useUIStore } from '@/stores/UIStore'
import { calculateWindowSize, timeout } from '@/utils/helpers'
import { useWindowSize } from '@vueuse/core'
import { onMounted, computed, watch, ref } from 'vue'
import { useRoute } from 'vue-router';
import AppLoading from "@/components/Simple/AppLoading.vue"
import { storeToRefs } from 'pinia';
import gpLocations from '@/utils/gp/locations';
import { env } from '@/config/env';
const { VITE_API_V1_MAIN_DOMAIN } = env;

/**
 * The main VUE component
 */
export default {
	components: {
		AppLoading
	},
	setup() {
		const isAppLoading = ref(true)
		const uiStore = useUIStore()
		const APIStore = useAPIStore()
		const authStore = useAuthStore()
		const route = useRoute();

		//determines bootstrap window size
		const windowSize = computed(() => {
			const { width } = useWindowSize()
			return calculateWindowSize(width.value)
		})

		//updates window size in uiStore
		watch(windowSize, async (newValue) => {
			if (uiStore.screenSize !== newValue) {
				uiStore.setWindowSize(newValue)
			}
		})

		// keep hash
		const scrollFix = (routeVal: any) => {
			if (routeVal.hash != "") location.hash = routeVal.hash;
		}

		onMounted(async () => {
			// load the hash after the route has finished loading
			setTimeout(() => scrollFix(route), 1);

			// handle redirect
			authStore.handleRedirectDomain('/', false)

			// get current organization
			await APIStore.getOrganization()

			// If logged in get more information about the org and user
			if(authStore.user){
				const userID = `${authStore.user.id}`

				try{
					await Promise.all([
						APIStore.getOrganizationStrategy(),
						APIStore.getUserByID(userID),
						APIStore.getOrganizationModulePermissions(),
						APIStore.getGranularPermissions(),
						APIStore.fetchGranularPermissions('locations', gpLocations.getFn),
					])
				}catch(e){
					authStore.logout()
				}

                // Prefetch the user's other tenants so the
                // "Switch tenant" dropdown opens instantly.
                await APIStore.getMyTenants()
				await APIStore.getMessages()
			}      

			// Mark app as finished loading
			isAppLoading.value = false

			// Modify favicon to this organization favicon
			if (APIStore.organization.favicon_logo) {
				(document.getElementById("pollux-app-favicon") as any).href = APIStore.organization.favicon_logo
			}

		})

		watch(route, async (currentRoute) => {
			setTimeout(() => scrollFix(currentRoute), 1);
		})

		// Key down event handler
		const handleKeyDown = (event) => {
			if (event.key == "Shift") {
				uiStore.shiftPressed = true
			}
		}

		// Key up event handler
		const handleKeyUp = (event) => {
			if (event.key == "Shift") {
				uiStore.shiftPressed = false
			}

		}

		// monitor events
		window.onkeydown = handleKeyDown
		window.onkeyup = handleKeyUp

		return {
			isAppLoading,
		}
	}

}
</script>
<style lang="scss">
.app-loading {
	display: flex;
	position: absolute;
	top: 0px;
	left: 0px;
	width: 100vw;
	height: 100vh;
	justify-content: center;
	align-items: center;

}

.loading-content {
	display: flex;
	height: calc(100vh - var(--Header-Height) - var(--Footer-Height) - 156px - 80px);
	justify-content: center;
	align-items: center;
	flex-direction: column;
	gap: 32px;
	text-align: center;
}
</style>
